import { Button, Form, Modal, Row, Space } from "antd";
import PlamoApiWithSaveNotifications from "../../util/with-save-notification";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import FormItem, { createRegexRule, REGEX_EMAIL } from "../../form/form-item";
import { AdvancedForm } from "../../form/advanced-form";
import { useResetPasswordMutation, useGetSalariesQuery, useGetTeamsQuery } from "../../../../api/plamo-api";
import dayjs from "dayjs";
import ConfirmPasswordButton from "../../util/confirm-password-button";
import { useGetGroupsQuery, useGetUserGroupsQuery } from "../../../../api/auth-api";
import { skipToken } from "@reduxjs/toolkit/query";

const { useCreateEmployeeWithSaveNotificationMutation, useUpdateEmployeeWithSaveNotificationMutation, useDeleteEmployeeWithSaveNotificationMutation } = PlamoApiWithSaveNotifications;

const useGetGroupsAsItemsQuery = () => {
  const result = useGetGroupsQuery(undefined) as any;
  const items = Array.isArray(result.data) ? result.data : result.data?.items;
  return { ...result, data: items ? { items } : undefined };
}

const groupsTypeData = { query: useGetGroupsAsItemsQuery, getId: group => group.id, render: group => `${group.name}`, multiple: true };

function EmployeeMainForm({ employeeId, employee }) {
  const [createEmployee, { isLoading: isCreatingEmployee }] = useCreateEmployeeWithSaveNotificationMutation()
  const [updateEmployee, { isLoading: isUpdatingEmployee }] = useUpdateEmployeeWithSaveNotificationMutation()
  const [deleteEmployee] = useDeleteEmployeeWithSaveNotificationMutation();
  const { data: salaries } = useGetSalariesQuery({ f: JSON.stringify({ "employee.id": { c: "EQ", v: employeeId } })}) as any;
  const salaryPerHour = useMemo(() => salaries?.items?.filter(s => (!s.startDate || dayjs(s.startDate).valueOf() <= dayjs().valueOf()) && (!s.endDate || dayjs(s.endDate).valueOf() >= dayjs().valueOf()))[0]?.salaryPerHour, [salaries?.items])
  const jobType = useMemo(() => salaries?.items?.filter(s => (!s.startDate || dayjs(s.startDate).valueOf() <= dayjs().valueOf()) && (!s.endDate || dayjs(s.endDate).valueOf() >= dayjs().valueOf()))[0]?.jobType, [salaries?.items])
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const externalId = Form.useWatch("externalId", form);
  const [callResetPassword] = useResetPasswordMutation();
  //const jobType = Form.useWatch("jobType", form);
  const addressZipCode = Form.useWatch("addressZipCode", form);
  const [newPassword, setNewPassword] =  useState<string | null>(null);
  const { data: userGroups } = useGetUserGroupsQuery(employee?.externalId ? { id: employee.externalId } : skipToken) as any;
  const originalValues = useMemo(() => employee && { ...employee, groupIds: userGroups?.map(g => g.id) ?? [] }, [employee, userGroups]);

  const isLoading = isCreatingEmployee || isUpdatingEmployee;

  const onFinish = useCallback(async (values: any) => {
    if(!employeeId) {
      const { data: newEmployee }:any = await createEmployee(values);
      console.log("Created new employee", newEmployee);
      navigate(`./../${newEmployee.id}`)
    } else {
      const newEmployee = {...employee, ...values}
      await updateEmployee(newEmployee);
    }
  }, [employeeId, createEmployee, updateEmployee, navigate, employee]);

  useEffect(() => {
    let limit;
    if(jobType === "Minijob") {
      limit = 556;
    } else if(jobType === "Midijob") {
      limit = 2000;
    }
    if(limit && salaryPerHour) {
      const worktimePerMonth = Math.floor(limit / salaryPerHour * 4) / 4;
      const worktimePerWeek = Math.floor(worktimePerMonth / 4.3 * 4) / 4;
      const values = form.getFieldsValue();
      form.setFieldsValue({...values, worktimePerMonth, worktimePerWeek});
    }
  }, [form, jobType, salaryPerHour])

  useEffect(() => {
      (async () => {
        try {
          if(addressZipCode?.match(/^\d{5}$/)) {
            let values = form.getFieldsValue();
            form.setFieldsValue({...values, addressCity: null, addressArea: null});
  
            const response = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${addressZipCode}&country=Deutschland&format=json&addressdetails=1&&accept-language=de`)
            const data = await response.json();
  
            const addressCity = data[0]?.address?.city || data[0]?.address?.village;
            const addressArea = data[0]?.address?.city_district || data[0]?.address?.suburb
  
            values = form.getFieldsValue();
            if(!values.addressCity?.length) {
              values = {...values, addressCity }
            }
            if(!values.addressArea?.length) {
              values = {...values, addressArea }
            }
            form.setFieldsValue(values);
          }
        } catch(e) {
          console.error("Could not fetch city for zip code", e);
        }
      })()
    }, [addressZipCode, form])

  const onDelete = useCallback(async () => {
    await deleteEmployee({ id: employeeId });
    navigate('/employee');
  }, [employeeId, deleteEmployee, navigate])

  const generatePassword = useCallback(async () => {
    const { data } = await callResetPassword({ id: employeeId }) as any;
    setNewPassword(data.value);
  }, [employeeId, callResetPassword, setNewPassword]);

  return (
    <>
    <Modal
        title="Neues Passwort erstellt"
        open={!!newPassword}
        onCancel={() => setNewPassword(null)}
        footer={(
          <>
            <Button type="primary" onClick={() => { setNewPassword(null); }}>OK</Button>
          </>
        )}
        
      >
        <>
        <p>Das neue Passwort lautet: <strong>{newPassword}</strong></p>
        <p>Bitte notieren Sie sich das Passwort, da es aus Sicherheitsgründen nicht erneut angezeigt werden kann.</p>
        </>
    </Modal>
    <AdvancedForm form={form} name="customer" onFinish={onFinish} isLoading={isLoading} originalValues={originalValues} initialEditing={!employeeId}>
    <Row><h3>Persönliche Daten</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="salutation" label="Anrede" type="backendEnum" typeData={{ ident: "salutation" }} />
      <FormItem fieldName="firstName" label="Vorname" />
      <FormItem fieldName="lastName" label="Nachname" />
      <FormItem fieldName="dateOfBirth" label="Geburtsdatum" type="date" />
      <FormItem fieldName="employeeId" label="Personalnummer" rules={[createRegexRule(/^\d+$/, 'Keine gültige Personalnummer')]} />
    </Row>
    <Row gutter={24}>
      <FormItem fieldName="addressStreet" label="Straße" />
      <FormItem fieldName="addressHouseNumber" label="Hausnummer" />
      <FormItem fieldName="addressZipCode" label="PLZ" rules={[createRegexRule(/^\d{5}$/, 'Keine gültige PLZ')]} />
      <FormItem fieldName="addressCity" label="Ort" />
      <FormItem fieldName="addressArea" label="Stadtteil" />
    </Row>
    <Row gutter={24}>
      <FormItem fieldName="mobilePrivate" label="Handy privat" />
      <FormItem fieldName="mobileWork" label="Handy dienstl." />
      <FormItem fieldName="emailPrivate" label="E-Mail privat" rules={[createRegexRule(REGEX_EMAIL, 'Keine gültige E-Mail-Adresse')]} />
      <FormItem fieldName="emailWork" label="E-Mail dienstl." rules={[createRegexRule(REGEX_EMAIL, 'Keine gültige E-Mail-Adresse')]} />
    </Row>
    <Row><h3>Soziale Daten</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="handicap" label="Schwerbehinderung" type="number" typeData={{ max: 100, addonAfter: '%' }} />
      <FormItem fieldName="childCount" label="Kinder" type="number" />
      <FormItem fieldName="bankAccount" label="IBAN" rules={[createRegexRule(/^[A-Z]{2}\d{20}$/, 'Keine gültige IBAN')]} />
      <FormItem fieldName="taxId" label="Steuer-ID" rules={[createRegexRule(/^\d{11}$/, 'Keine gültige Steuer-ID')]} />
      <FormItem fieldName="taxClass" label="Steuerklasse" type="backendEnum" typeData={{ ident: "taxClass" }} />
    </Row>      
    <Row gutter={24}>
      <FormItem fieldName="insurance" label="Krankenkasse" type="backendEnum" typeData={{ showSearch: true, ident: "insurance" }} />
      <FormItem fieldName="socialSecurityNumber" label="SV-Nummer" rules={[createRegexRule(/^\d{8}\w{1}\d{3}$/, 'Keine gültige SV-Nummer')]} />
    </Row>
    <Row><h3>Vertragsdaten</h3></Row>
    <Row gutter={24}>
      { false && <FormItem fieldName="jobType" label="Funktion" type="backendEnum" typeData={{ showSearch: true, ident: "jobType" }} /> }
      { employeeId ? <FormItem fieldName="xxx" label="Funktion" type="staticLink" typeData={{ value: jobType ?? 'Nicht angegeben', onClick: () => navigate(`/employee/${employeeId}/salary`) }} /> : <FormItem fieldName="jobType" label="Funktion" type="backendEnum" typeData={{ showSearch: true, ident: "jobType" }} /> }
      <FormItem fieldName="jobDescription" label="Jobbezeichnung" type="backendEnum" typeData={{ showSearch: true, ident: 'jobDescription', multiple: true }} />        
      <FormItem fieldName="education" label="Ausbildung" />
      { employeeId ? <FormItem fieldName="xxx" label="Stundenlohn" type="staticLink" typeData={{ value: salaryPerHour ? `${salaryPerHour.toFixed(2).replace('.', ',')} €` : 'Nicht angegeben', onClick: () => navigate(`/employee/${employeeId}/salary`) }} /> : <FormItem fieldName="salaryPerHour" label="Stundenlohn" type="currency" /> }
      <FormItem fieldName="grossSalary" label="Bruttogehalt" type="currency" />
    </Row>
    <Row gutter={24}>
      <FormItem disabled={["Minijob", "Midijob"].includes(jobType)} fieldName="worktimePerWeek" label="wöchentl. Arbeitszeit" type="number" />
      <FormItem disabled={["Minijob", "Midijob"].includes(jobType)} fieldName="worktimePerMonth" label="monatl. Arbeitszeit" type="number" />
    </Row>
    <Row gutter={24}>
      <FormItem fieldName="dateOfEntry" label="Eintritt" type="date" />
      <FormItem fieldName="dateOfProbation" label="Ende Probezeit" type="date" />
      <FormItem fieldName="limitation" label="Ende Befristung" type="date" />
      <FormItem fieldName="dateOfExit" label="Austritt" type="date" />
    </Row>
    <Row><h3>Urlaub</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="vacationDays" label="Urlaub / Jahr" type="number" />
      <FormItem fieldName="vacationDaysReferenceTimestamp" label="Referenzdatum" type="date" />
      <FormItem fieldName="vacationDaysAtReferenceTimestamp" label="Resturlaub Ref-Datum" type="date" />
    </Row>
    <Row><h3>Sonstiges</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="car" label="Auto" type="enum" typeData={{ options: [{ value: "y", label: "Ja" }, { value: "n", label: "Nein" }] }}  />
      <FormItem fieldName="allergies" label="Allergien" />
      <FormItem fieldName="animalPhobias" label="Phobien" />
    </Row>
    <Row><h3>Status</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="capacity" label="Kapazitäten" type="enum" typeData={{ options: [{ value: "y", label: "Ja" }, { value: "n", label: "Nein" }] }}  />
      <FormItem fieldName="active" label="Aktiv" type="check" />
      <FormItem fieldName="teamId" label="Teamzugehörigkeit" type="ref" typeData={{ query: useGetTeamsQuery, getId: team => team.id, render: team => `${team.name}` }} />
      <FormItem fieldName="teamLead" label="Teamleitung" type="check" />
    </Row>
    <Row><h3>Login</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="externalId" label="Ext. Id" readOnly />
      <FormItem fieldName="loginUsername" label="Login" readOnly />
      <FormItem fieldName="loginEnabled" label="Login aktiv" type="check" />
      <FormItem fieldName="groupIds" label="Gruppen" type="ref" typeData={groupsTypeData} />
      { externalId && <FormItem fieldName="regeneratePassword" label="Neues Passwort" type="button" typeData={{ caption: "Erstellen", onClick: generatePassword }} /> }
    </Row>
    { employeeId && <>
      <Row><h3>Mitarbeiter löschen</h3></Row>
      <div>
        <Space size="small">
          <ConfirmPasswordButton type="default" danger onClick={onDelete} confirmTitle={`Mitarbeiter ${employee?.firstName} ${employee?.lastName} löschen?`} confirmContent={'Bitte geben Sie das Passwort ein, um den Mitarbeiter zu löschen:'}>
            Mitarbeiter löschen
          </ConfirmPasswordButton>
        </Space>
      </div>
    </>}
  </AdvancedForm>
  </>)
}

export default EmployeeMainForm;